import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { X, BookOpen, FilePdf, FolderOpen, PencilLine, Plus } from '@phosphor-icons/react';
import useUserStore from '../../stores/userStore';
import useSystemStore from '../../stores/systemStore';
import useDataspaceTreeStore from '../../stores/dataspaceTreeStore';
import { setupBookProject } from '../../services/projectSetupService';
import { toastService } from '../../services/ui/toastService';
import styles from './BookProjectPromoModal.module.scss';

interface BookProjectPromoModalProps {
  onClose: () => void;
}

const BOOK_PROJECT_NAME = 'My Novel';

const BookProjectPromoModal: React.FC<BookProjectPromoModalProps> = ({ onClose }) => {
  const [isCreating, setIsCreating] = useState(false);
  const setOnboardingFlag = useUserStore(state => state.setOnboardingFlag);
  const navigate = useNavigate();

  const handleDismiss = () => {
    setOnboardingFlag('bookProjectPromoModalShown', true);
    onClose();
  };

  const handleCreateBook = async () => {
    setIsCreating(true);
    try {
      const newSpace = await useSystemStore.getState().actions.createSpace({
        name: BOOK_PROJECT_NAME,
        skipDefaultWindow: true,
        skipProjectLimit: true,
        settings: {
          isChatVisible: true,
          sidebar: { isVisible: true, width: 260, mode: 'normal' }
        }
      });
      if (!newSpace?.id) throw new Error('Space created but missing ID');
      const spaceId = newSpace.id;

      // Fire-and-forget: setup runs in background while we navigate (matches SpaceCreationPage flow)
      setupBookProject(spaceId, BOOK_PROJECT_NAME)
        .then(() => useDataspaceTreeStore.getState().refreshEdges())
        .catch(err => console.warn('[BookProjectPromoModal] Setup error (non-fatal):', err));

      setOnboardingFlag('bookProjectPromoModalShown', true);
      onClose();
      navigate(`/spaces/${spaceId}`);
    } catch (error) {
      console.error('[BookProjectPromoModal] Failed to create book project:', error);
      toastService.error('Failed to create project. Please try again.');
      setIsCreating(false);
    }
  };

  return createPortal(
    <div className={styles.overlay} onClick={handleDismiss}>
      <motion.div
        className={styles.modal}
        onClick={(e) => e.stopPropagation()}
        initial={{ opacity: 0, y: 24, scale: 0.96 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.25, ease: [0.4, 0, 0.2, 1] }}
      >
        <button className={styles.closeButton} onClick={handleDismiss} aria-label="Close">
          <X size={16} weight="bold" />
        </button>

        <div className={styles.iconBadge}>
          <BookOpen size={26} weight="duotone" />
        </div>

        <h2 className={styles.title}>Start Your Book Project</h2>
        <p className={styles.subtitle}>
          Bring your book to life. Write, research, outline, and organize everything in one dedicated project.
        </p>

        {/* Mock screenshot of the book formatter */}
        <div className={styles.mockScreenshot}>
          <div className={styles.mockTitleBar}>
            <div className={styles.mockDots}>
              <span /><span /><span />
            </div>
            <span className={styles.mockWindowTitle}>My Novel — Book Project</span>
          </div>
          <div className={styles.mockBody}>
            <div className={styles.mockSidebar}>
              <div className={styles.mockSidebarLabel}>Chapters</div>
              <div className={`${styles.mockChapter} ${styles.mockChapterActive}`}>
                <BookOpen size={11} weight="fill" />
                Prologue
              </div>
              <div className={styles.mockChapter}>
                <BookOpen size={11} weight="regular" />
                Chapter 1
              </div>
              <div className={styles.mockChapter}>
                <BookOpen size={11} weight="regular" />
                Chapter 2
              </div>
              <div className={styles.mockChapter}>
                <BookOpen size={11} weight="regular" />
                Chapter 3
              </div>
              <div className={styles.mockAddChapter}>
                <Plus size={10} weight="bold" />
                Add chapter
              </div>
            </div>
            <div className={styles.mockContent}>
              <div className={styles.mockContentTitle}>Prologue</div>
              <div className={styles.mockLine} style={{ width: '92%' }} />
              <div className={styles.mockLine} style={{ width: '85%' }} />
              <div className={styles.mockLine} style={{ width: '78%' }} />
              <div className={styles.mockLine} style={{ width: '88%' }} />
              <div className={styles.mockLine} style={{ width: '60%' }} />
            </div>
          </div>
        </div>

        {/* Feature list */}
        <ul className={styles.features}>
          <li className={styles.feature}>
            <span className={styles.featureIcon}><FolderOpen size={14} weight="duotone" /></span>
            <span>Dedicated space for drafts, outlines &amp; research</span>
          </li>
          <li className={styles.feature}>
            <span className={styles.featureIcon}><PencilLine size={14} weight="duotone" /></span>
            <span>AI writing assistance throughout your whole project</span>
          </li>
          <li className={styles.feature}>
            <span className={styles.featureIcon}><FilePdf size={14} weight="duotone" /></span>
            <span>Format &amp; export to a print-ready PDF when you're done</span>
          </li>
        </ul>

        <button
          className={styles.ctaButton}
          onClick={handleCreateBook}
          disabled={isCreating}
        >
          <BookOpen size={16} weight="duotone" />
          {isCreating ? 'Creating...' : 'Create Book Project'}
        </button>

        <button className={styles.dismissLink} onClick={handleDismiss}>
          Maybe later
        </button>
      </motion.div>
    </div>,
    document.body
  );
};

export default BookProjectPromoModal;
